import * as React from 'react';
import { Page } from '../../cookbook-ui/Page_SC';
import { SnackBar, SearchBox, SnackBarProps } from '@inmoment/the-kitchen';
import { VisualTile } from '../../cookbook-ui/visual-components/VisualTile/VisualTile';
import { Link } from 'react-router-dom';
import { CodeBlock } from '../../cookbook-ui/custom-components/CodeBlock/CodeBlock';
import { Blank } from '../intro-pages/Home/FAQ_SC';
import { GraphicAppIcon } from '../../cookbook-ui/visual-components/automated-components/graphics/GraphicAppIcon';
import { GraphicIcon } from '../../cookbook-ui/visual-components/automated-components/graphics/GraphicIcon';
import { _AppIconMeta, _IconMeta } from '../../cookbook-ui/visual-components/automated-components/graphics/metaExports';

export const Icons = () => {
  const [query, setQuery] = React.useState('');
  const [appArray, setAppArray] = React.useState<typeof _AppIconMeta>([]);
  const [iconArray, setIconArray] = React.useState<typeof _IconMeta>([]);

  const [snackBarObject, setSnackBarObject] = React.useState<SnackBarProps | null>(null);
  const textArea = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const scroll = () => {
      const lastBit = window.location.href.split('/').slice(-1)[0];
      if (lastBit.split('#').length > 1) {
        const element = document.querySelector(`#${lastBit.split('#')[1]}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('load', scroll);
    return () => window.removeEventListener('load', scroll);
  }, []);

  function handleClick(text: string) {
    if (textArea.current) textArea.current.value = text;
    textArea.current?.select();
    document.execCommand('copy');
    setSnackBarObject({
      mode: 'default',
      text: `${text} copied to clipboard`,
      dismissTime: 5000,
      autoDismiss: true,
      id: new Date().getTime().toString(),
    });
  }

  React.useEffect(() => {
    const sort = (i: (typeof _AppIconMeta)[number] | (typeof _IconMeta)[number]) => {
      if (i) {
        let containsTag = false;
        i.tags.some((tag: string) => {
          if (tag.includes(query)) {
            containsTag = true;
            return true;
          } else {
            return false;
          }
        });
        return containsTag || i.name.toLowerCase().includes(query.toLowerCase());
      }
    };
    setAppArray(_AppIconMeta.filter(sort));
    setIconArray(_IconMeta.filter(sort));
  }, [query]);

  return (
    <Page>
      <p className="kxi-text-xlarge kxi-text-600">Icons</p>
      <br />
      <div style={{ display: 'flex' }}>
        <p style={{ flex: 1 }}>These icons are built on a 24 pixel grid. Click on an icon to copy the name to the clipboard.</p>
        <div style={{ width: 260, marginLeft: 24 }}>
          <SearchBox placeholder="search by name or tag" onChange={(e) => setQuery(e.target.value)} onCancelClick={() => setQuery('')} />
        </div>
      </div>
      <hr id="App-Icons" />
      <br />
      <br />
      {appArray.length > 0 && (
        <>
          <p className="kxi-text-large kxi-text-600">App Icons</p>
          <br />
          <p>
            Use the <Link to="/Visuals">XIAppIcon</Link> component to access these svgs.
          </p>
          <br />
          <br />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
              gridAutoRows: '1fr',
            }}
          >
            {appArray
              .sort((a, b) => {
                let aIncludesWhite = a.name.includes('white') || a.name.includes('kitchen');
                let bIncludesWhite = b.name.includes('white') || b.name.includes('kitchen');
                if (aIncludesWhite && !bIncludesWhite) {
                  return 1;
                } else if (!aIncludesWhite && bIncludesWhite) {
                  return -1;
                } else {
                  return 0;
                }
              })
              .map((icon) => {
                return (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    color={icon.name.includes('white') || icon.name.includes('kitchen') ? 'orca' : 'white'}
                    downloadPath={`assets/kitchen/v2/app-icons/${icon.name}.svg`}
                    onClick={() => handleClick(icon.name)}
                  >
                    <GraphicAppIcon appIcon={icon.name} />
                  </VisualTile>
                );
              })}
          </div>
          <br />
          <br />
          <br />
        </>
      )}
      {iconArray.length > 0 && (
        <>
          <br />
          <br />
          <hr id="XI-Icons" />
          <br />
          <br />
          <p className="kxi-text-large kxi-text-600">XI Icons</p>
          <br />
          <p>
            Use the <Link to="/Visuals">XIIcon</Link> component to access these svgs.
          </p>
          <br />
          <br />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
              gridAutoRows: '1fr',
            }}
          >
            {iconArray.map((icon) => {
              return (
                <VisualTile
                  key={icon.name}
                  text={icon.name}
                  onClick={() => handleClick(icon.name)}
                  downloadPath={`assets/kitchen/v2/icons/${icon.name}.svg`}
                >
                  <GraphicIcon icon={icon.name} />
                </VisualTile>
              );
            })}
          </div>
        </>
      )}
      {appArray.length === 0 && iconArray.length === 0 && <p>No results found.</p>}
      <br />
      <hr id="Modifying-Color" />
      <br />
      <br />
      <p className="kxi-text-large kxi-text-600">Modifying Color</p>
      <br />
      <p>
        If you're using the Kitchen, please reference the <Link to="/Visuals#XIIcon">XIIcon</Link> component to change an icon's color.
      </p>
      <br />
      <br />
      <p className="kxi-text-300 kxi-text-normal">Modifying a raw svg files fill (color)</p>
      <br />
      <CodeBlock>
        {`<svg ... `}
        <b>{`fill="#HEX_CODE"`}</b>
        {`>`}
        <br />
        &nbsp;&nbsp;
        {`<path ... />`}
        <br />
        {`</svg>`}
      </CodeBlock>
      <br />
      <p>
        You can find a list of Garage Approved Hex Codes on the <Link to="/Colors#Colors">Color</Link> page.
      </p>
      <Blank />
      <textarea style={{ position: 'fixed', top: 0, opacity: 0 }} ref={textArea} readOnly />
      <SnackBar {...snackBarObject} />
    </Page>
  );
};
